import type {
    RestaurantProductImpressionCategory,
    RestaurantProductImpressionData,
    LsVisitorCookie,
    Product,
} from 'root/core/lsVisitor.types';

import { CookieMonster } from 'root/utils/cookies/cookieMonster';
const cm = new CookieMonster();
const lsVisitorCookieName = 'lsVisitor';

class LsVisitor {
    #updateLsVisitorCookie(lsVisitorCookieData: LsVisitorCookie) {
        (<any>window).registerCookieComplianceCallback({
            callback: () =>
                cm.setCookie(lsVisitorCookieName, JSON.stringify(lsVisitorCookieData)),
            isCallOptanonWrapper: true,
        });
    }

    #getLsVisitorCookie() {
        return (cm.getCookie(lsVisitorCookieName) as LsVisitorCookie | null) ?? {};
    }

    getDynamicPagesCookieData() {
        const lsVisitorCookieData = this.#getLsVisitorCookie();
        return lsVisitorCookieData.dynamicPagesCookieData ?? {};
    }

    getRestaurantProduct() {
        const lsVisitorCookie = this.#getLsVisitorCookie();
        // impressionCategoryPrecedence is ordered from strongest (at the beginning with highest number) to weakest (at the end with lowest number)
        const impressionCategoryPrecedence = new Map([
            [3, 'loggedInAs'], // when the user logs into a restaurant product from our site
            [2, 'URLContains'], // when the url contains a specific value
            [1, 'integrationsChoice'], // when the user chooses their series on the integrations page
            [0, 'integrationsLanding'], // when the user lands on a specific integration page that only is available for one product, we can set it on page load
        ]).values();
        const restaurantProductImpressions =
            lsVisitorCookie.restaurantProductImpressions ?? {};
        let restaurantProduct;
        for (const impressionCategory of impressionCategoryPrecedence) {
            const series =
                restaurantProductImpressions[
                    impressionCategory as keyof RestaurantProductImpressionData
                ];
            if (series) {
                restaurantProduct = series;
                break;
            }
        }
        return restaurantProduct ?? 'ikentoo';
    }

    setRestaurantProductImpression(
        impressionCategory: RestaurantProductImpressionCategory,
        series: string
    ) {
        const lsVisitorCookieData = this.#getLsVisitorCookie();

        // In some cases we may be forced to use alternate names for the series, but this map ensures that they'll be saved internally to be the same value
        const internalSeriesByUserFacingSeries = new Map([
            ['k-series', 'ikentoo'],
            ['g-series', 'gastrofix'],
        ]);
        const internalSeriesValue =
            internalSeriesByUserFacingSeries.get(series) ?? series;
        lsVisitorCookieData.restaurantProductImpressions = {
            ...lsVisitorCookieData.restaurantProductImpressions,
            [impressionCategory]: internalSeriesValue,
        };
        this.#updateLsVisitorCookie(lsVisitorCookieData);
    }

    setBusinessType(businessType: string, product: Product) {
        const lsVisitorCookieData = this.#getLsVisitorCookie();
        lsVisitorCookieData.businessType = {
            ...lsVisitorCookieData.businessType,
            [product]: businessType,
        };
        this.#updateLsVisitorCookie(lsVisitorCookieData);
    }
    getBusinessType(product: Product) {
        const lsVisitorCookieData = this.#getLsVisitorCookie();
        return lsVisitorCookieData.businessType?.[product] ?? null;
    }
}
export { LsVisitor };
